<!--

  v1.0
  Composant: b122-table

  Affichage des résultats
  - Limiter les éléments affichés (défaut : tous les éléments de la table en BDD)
  - Changer les en-têtes de colonnes (défaut : les en-têtes de colonnes correspondent aux propriétés de l'objet en BDD)
  - Formatter les valeurs affichées dans la ligne (défaut : les valeurs brutes de la BDD sont affichées pour chaque résultat)

  Tri
  - Changer le tri initial (défaut : id desc)
  - Ajouter la possibilité de trier au niveau de l'en-tête de colonne (défaut: non actif)

  Actions
  - Afficher ou non l'oeil à gauche pour le show (défaut : caché)
  - Afficher ou non les actions edition et suppression (défaut : caché)
  - Possibilité de surcharger les boutons d'action (défaut : non actif)

  Pagination
  - Nombre de résultats par page utilisé par défaut (défaut : 50)
  - Options possibles pour le nombre de résultats par page (défaut : 0, 50, 100, Tout)

  Recherche
  - Propriétés de l'objet sur lesquelles se fait la recherche (défaut: id)

  Filtres
  - Possiblité d'ajouter des filtres pour limiter les résultats affichés par objet parent, par propriété... (défaut: non actif)

  Persistance de l'affichage
  - Les modifications d'affichage du tableau (tri, recherche, filtre) sont conservées tant que l'utilisateur est connecté.
  - Un bouton permet de réinitialiser l'affichage. (edited)
-->
<template>

  <div class="b122-table-container">

    <div class="d-flex flex-column-reverse flex-lg-row justify-content-lg-between mb-2">

      <div class="d-lg-flex">
        <div v-for="filter in filters" class="mr-0 mr-lg-2 mb-2 mb-lg-0">
          <vc-date-picker
            v-if="filter.type === 'datepicker'"
            v-model="filter.selectedValue"
            is-range
            color="orange"
            :popover="{ visibility: 'click' }"
            :first-day-of-week="2"
            class="d-flex flex-row"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <input
                class="border px-1 py-1 rounded datepicker-B122-table"
                :class="{'active': filter.selectedValue}"
                :value="inputValue.start ? inputValue.start+' - '+inputValue.end : filter.options[0].text"
                v-on="inputEvents.start"
              />
              <square-x-icon
                v-if="filter.selectedValue"
                class="datepicker-reset-icon"
                @click="filter.selectedValue = null"
              ></square-x-icon>
              <input
                class=""
                :value="inputValue.end"
                v-on="inputEvents.end"
                hidden
              />
            </template>
          </vc-date-picker>
          <b-form-select
            v-else
            class="filter-select"
            :class="{'active': filter.selectedValue}"
            v-model="filter.selectedValue"
            :options="filter.options"
          ></b-form-select>
        </div>
      </div>

      <div class="text-right mb-2 mb-lg-0">
        <b-link class="reset-link" @click="resetTable">
          {{ $t('ResetDisplay') }}
        </b-link>
      </div>

    </div>

    <div class="d-flex flex-column flex-md-row justify-content-md-between mb-4">

      <b-input-group
        class="mr-md-4"
        size="sm"
      >
        <b-input-group-prepend>
          <b-input-group-text class="search-input-prepend">
            <search-icon size="18"/>
          </b-input-group-text>
        </b-input-group-prepend>
        <b-form-input
          class="search-input"
          :class="{'active': search}"
          v-model="search"
          type="search"
          :laceholder="$t('Research')"
        ></b-form-input>
      </b-input-group>

      <b122-table-pagination
        class="mt-2 mt-lg-0"
        :currentPage="currentPage"
        :perPage="perPage"
        :perPageOptions="perPageOptions"
        :totalRows="totalRows"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      />

    </div>

    <div class="mb-4 overflow-auto">

      <b-table
        ref="b122Table"
        :api-url="getB122TableResultsUrl"
        :items="provider"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        :busy="isBusy"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        stacked="lg"
        empty-text="Aucun résultat"
        show-empty
        striped
        borderless
        sort-icon-left
      >

        <template #table-busy>
          <div class="busy">
            <b-spinner style="width: 3em; height: 3em;"></b-spinner>
          </div>
        </template>

        <template #empty="scope">
          <div class="no-result">
            {{ scope.emptyText }}
          </div>
        </template>

        <template #cell(show)="row" v-if="!overrideShowCell">
          <button class="action-btn" @click="$emit('clickOnShowBtn', row)">
            <eye-icon size="18"/>
          </button>
        </template>

        <template v-for="(_, slotName) of $scopedSlots" v-slot:[slotName]="scope">
          <slot :name="slotName" v-bind="scope"/>
        </template>

        <!-- Afficher actions par défaut si pas de surcharge -->
        <template #cell(actions)="row" v-if="!overrideActionsCell">
          <div class="actions-container text-right">
            <button class="action-btn mr-1" @click="$emit('clickOnEditBtn', row)" v-if="!deleteOnly && !downloadOnly">
              <pencil-icon size="18"/>
            </button>
            <button class="action-btn" @click="$emit('clickOnDeleteBtn', row)" v-if="!editOnly && !downloadOnly">
              <trash-icon size="18"/>
            </button>
            <button class="action-btn" @click="$emit('clickOnDownloadBtn', row)" v-if="downloadOnly">
              <download-icon size="18"/>
            </button>
          </div>
        </template>

      </b-table>
    </div>

    <div class="d-flex justify-content-end">
      <b122-table-pagination
        :currentPage="currentPage"
        :perPage="perPage"
        :perPageOptions="perPageOptions"
        :totalRows="totalRows"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"
      />
    </div>

  </div>
</template>

<script>

import _ from "lodash";
import api from "../../services/api";
import B122TablePagination from "./B122TablePagination";
import {mapActions} from "vuex";

export default {

  name: 'b122-table',

  components: {
    B122TablePagination
  },

  props: {

    identifier: {
      type: String,
      required: true
    },

    // Data

    initB122TableUrl: {
      type: String,
      required: true
    },

    getB122TableResultsUrl: {
      type: String,
      required: true
    },

    fields: {
      type: Array
    },

    // Actions

    editOnly: {
      type: Boolean,
      default: false
    },

    downloadOnly: {
      type: Boolean,
      default: false
    },

    deleteOnly: {
      type: Boolean,
      default: false
    },

    // Search

    initialSearch: {
      type: String,
      default: null
    },

    // Sort

    initialSortBy: {
      type: String,
      default: 'id'
    },

    initialSortDesc: {
      type: Boolean,
      default: true
    },

    // Paginate

    initialCurrentPage: {
      type: Number,
      default: 1
    },

    initialPerPage: {
      type: Number,
      default: 50
    },

    initialPerPageOptions: {
      type: Array,
      default() {
        return [
          {
            value: 10,
            text: "10 " + this.$i18n.t('perPage')
          },
          {
            value: 50,
            text: "50 " + this.$i18n.t('perPage')
          },
          {
            value: 100,
            text: "100 " + this.$i18n.t('perPage')
          },
          {
            value: 2147483647, // Max integer value
            text: this.$i18n.t('seeEverything')
          }
        ]
      }
    }
  },

  emits: [
    'setTotalCount',
    'clickOnShowBtn',
    'clickOnEditBtn',
    'clickOnDeleteBtn'
  ],

  data() {
    return {

      // Search
      search: this.initialSearch, // keep

      // Sort
      sortBy: this.initialSortBy, // keep
      sortDesc: this.initialSortDesc, // keep

      // Paginate
      currentPage: this.initialCurrentPage, // keep
      perPage: this.initialPerPage, // keep
      perPageOptions: this.initialPerPageOptions,

      // Others
      filters: [], // keep
      debouncedRefreshTable: null,
      totalRows: 0,
      isBusy: false,
    }
  },

  computed: {

    overrideShowCell() {
      return this.$scopedSlots['cell(show)']
    },

    overrideActionsCell() {
      return this.$scopedSlots['cell(actions)']
    }

  },

  watch: {

    'search': function (newValue, oldValue) {
      this.debouncedRefreshTable()
    },

    'filters': {
      handler: function (newValue, oldValue) {
        this.debouncedRefreshTable()
      },
      deep: true
    }
  },

  created() {
    this.debouncedRefreshTable = _.debounce(this.refreshTable, 300)
    this.restoreStateFromStore()
  },

  mounted() {
    this.initB122Table()
  },

  beforeDestroy() {
    this.saveState()
  },

  methods: {

    ...mapActions(["persistTableState"]),

    changeCurrentPage(newCurrentPage) {
      this.currentPage = newCurrentPage
    },

    changePerPage(newPerPage) {
      this.perPage = newPerPage
      this.currentPage = 1
    },

    initB122Table() {
      api
        .get(this.initB122TableUrl)
        .then(response => {
          this.$emit('setTotalCount', response.data.totalCount)

          this.filters = response.data.filters

          const tableState = this.$store.getters.getStateForTable(this.identifier)
          if (tableState) {
            this.filters.map(filter => {
              const filterFromState = tableState.filters.filter(item => item.position === filter.position)
              if (filterFromState.length > 0) {
                filter.selectedValue = filterFromState[0].selectedValue ? filterFromState[0].selectedValue : ""
              }
            })
          }

        })
    },

    provider(ctx) {

      let formData = new FormData()
      formData.append('perPage', ctx.perPage)
      formData.append('currentPage', ctx.currentPage)
      formData.append('sortBy', ctx.sortBy)
      formData.append('sortDesc', ctx.sortDesc)

      if (this.search && this.search.trim() !== "") {
        formData.append('search', this.search.trim())
      }

      const filtersFormatted = this.filters.map((filter) => {
        return {
          'position': filter.position,
          'selectedValue': filter.selectedValue
        }
      });
      formData.append('filters', JSON.stringify(filtersFormatted))

      const promise = api({
        method: 'POST',
        url: ctx.apiUrl,
        data: formData
      })
      return promise.then((response) => {
        const items = response.data.items
        this.totalRows = response.data.totalRows
        return items || []
      })
    },

    refreshTable() {
      this.$refs.b122Table.refresh()
    },

    resetTable() {
      this.currentPage = 1
      this.perPage = this.initialPerPage
      this.search = this.initialSearch
      this.filters.map(filter => filter.selectedValue = "")
      this.sortBy = this.initialSortBy
      this.sortDesc = this.initialSortDesc
      this.refreshTable()
    },

    saveState() {
      this.persistTableState({
        identifier: this.identifier,
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        totalRows: this.totalRows,
        currentPage: this.currentPage,
        perPage: this.perPage,
        filters: this.filters.map(item => ({position: item.position, selectedValue: item.selectedValue}))
      })
    },

    restoreStateFromStore() {
      const tableState = this.$store.getters.getStateForTable(this.identifier)
      if (!tableState) {
        return
      }
      this.search = tableState.search
      this.sortBy = tableState.sortBy
      this.sortDesc = tableState.sortDesc
      this.totalRows = tableState.totalRows
      this.perPage = tableState.perPage
      this.currentPage = tableState.currentPage
    }
  }
}
</script>

<style scoped>

/*** Search ***/
.search-input {
  border-radius: 0.25rem;
  font-size: var(--content-font-size);
  max-width: 500px;
}

.b122-table-container >>> .search-input-prepend {
  border-radius: 0.25rem;
}

.search-input.active {
  border-color: var(--main-color);
}

/*** Filters ***/
.filter-select {
  border-radius: var(--main-border-radius);
  font-size: var(--content-font-size);
}

.filter-select.active {
  border-color: var(--main-color);
}

.datepicker-B122-table {
  font-size: var(--content-font-size);
  color: #495057;
  height: calc(1.5em + 0.75rem + 2px);
  border: 1px solid #ced4da !important;
  border-radius: 0.25rem !important;
  width: 100%;
  min-width: 210px;
}

.datepicker-B122-table.active {
  border-color: var(--main-color) !important;
}

.datepicker-reset-icon {
  margin-top: 5px;
  margin-left: -30px;
  color: #495057;
  cursor: pointer;
}

/*** Reset ***/
.reset-link {
  color: var(--main-color);
}

/*** Table ***/

.b122-table-container >>> .table-striped tbody tr:nth-of-type(odd) {
  background-color: var(--main-color-background);
}

.b122-table-container >>> th[role="columnheader"] {
  vertical-align: middle;
  color: #8b8d8f;
}

.b122-table-container >>> td[role="cell"] {
  vertical-align: middle;
}

.b122-table-container .no-result {
  display: flex;
  align-items: center;
  justify-content: center;
  color: grey;
  height: 100px;
}

.b122-table-container .busy {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--main-color);
  height: 100px;
  font-size: 1.2em;
}

.b122-table-container >>> td a {
  color: var(--main-text-color);
  text-decoration: underline;
}

@media (max-width: 992px) {
  .b122-table-container >>> td[role="cell"] {
    text-align: end;
    font-weight: bold;
  }

  .b122-table-container >>> td[role="cell"]::before {
    text-align: start !important;
    font-weight: initial !important;
  }
}


</style>

<style>

/*** Actions ***/
.actions-container {
  min-width: 80px;
}

.action-btn {
  background-color: var(--main-color);
  border: none;
  color: var(--main-text-color);
  border-radius: var(--main-border-radius);
  padding: 4px 8px;
}

.action-btn:hover {
  background-color: var(--main-color-hover);
}
</style>



// WEBPACK FOOTER //
// src/components/b122/B122Table.vue